import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Clock,
  BookOpen,
  TrendingUp,
  ChevronRight,
  MoreHorizontal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import { Badge } from '../../ui/badge';

interface ContinueLearningProps {
  enrollments: any[];
}

// Fonction utilitaire pour formater le niveau de difficulté
const formatDifficultyLevel = (level: string) => {
  switch (level) {
    case 'beginner':
      return 'Débutant';
    case 'intermediate':
      return 'Intermédiaire';
    case 'advanced':
      return 'Avancé';
    default:
      return 'Inconnu';
  }
};

const ContinueLearning: React.FC<ContinueLearningProps> = ({ enrollments }) => {
  const navigate = useNavigate();

  // Filtrer les cours actifs et les trier par dernière activité
  const activeCourses = enrollments
    .filter(enrollment => enrollment.status === 'active')
    .sort((a, b) => new Date(b.last_accessed || b.created_at).getTime() - new Date(a.last_accessed || a.created_at).getTime())
    .slice(0, 3);

  const handleContinueCourse = (courseSlug: string) => {
    navigate(`/elearning/courses/${courseSlug}`);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  if (activeCourses.length === 0) {
    return (
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BookOpen className="h-5 w-5 text-blue-500" />
            <span>Continuer l'apprentissage</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Aucun cours actif pour le moment
            </p>
            <Button 
              onClick={() => navigate('/elearning/courses')}
              className="mt-4"
            >
              Découvrir les cours
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center space-x-2">
          <Play className="h-5 w-5 text-green-500" />
          <span>Continuer l'apprentissage</span>
        </CardTitle>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => navigate('/elearning/student/courses')}
          className="text-yellow-500 hover:text-blue-600"
        >
          Voir tout
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          {activeCourses.map((enrollment) => {
            const course = enrollment.course;
            const progress = enrollment.progress_percentage || 0;
            
            return (
              <motion.div
                key={enrollment.id}
                variants={cardVariants}
                className="group"
              >
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-x-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 cursor-pointer"
                     onClick={() => handleContinueCourse(course.slug)}>
                  
                  {/* Course info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate pr-2">
                        {course.title}
                      </h3>
                      <Badge variant="default" className="text-xs">
                        {progress}%
                      </Badge>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{course.duration_hours || 0}h</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <TrendingUp className="h-4 w-4" />
                        <span>{formatDifficultyLevel(course.difficulty_level || 'beginner')}</span>
                      </div>
                      {course.instructor && (
                        <div className="flex items-center space-x-1">
                          <BookOpen className="h-4 w-4" />
                          <span>{course.instructor.name}</span>
                        </div>
                      )}
                    </div>

                    {/* Progress bar */}
                    <div className="space-y-1">
                      <Progress value={progress} className="h-2" />
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Progression</span>
                        <span>{progress}% terminé</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row w-full sm:w-auto justify-between items-center mt-4 sm:mt-0">
                    {/* Course thumbnail */}
                    <div className="relative w-1/2 sm:w-auto mb-4 sm:mb-0">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mx-auto">
                        {course.thumbnail_url ? (
                          <img 
                            src={course.thumbnail_url} 
                            alt={course.title}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <BookOpen className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
                        )}
                      </div>
                      
                      {/* Play button overlay */}
                      <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Play className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    {/* Action button */}
                    <Button
                      size="sm"
                      className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 w-1/2 sm:w-auto"
                      onClick={(e) => {
                      e.stopPropagation();
                      handleContinueCourse(course.slug);
                    }}
                  >
                    {progress === 0 ? 'Commencer' : 'Continuer'}
                  </Button>
                </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* View all button */}
        {activeCourses.length > 0 && (
          <div className="mt-6 text-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/elearning/student/courses')}
              className="w-full"
            >
              Voir tous mes cours
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ContinueLearning;
